/**
 * Every API path the web app knows, in one file.
 *
 * Nothing else in apps/web builds a path by string concatenation. The route
 * prefix conflict this term (foundation's `/api` in mesh #95 vs catalog's
 * `/api/v1` in #90, ruled `/api/v1` in #117) is exactly the class of bug that
 * costs nothing to fix here and is a 404 in production otherwise -- and it is
 * only cheap because there is one place to change.
 *
 * Paths are relative to the /api/v1 prefix that `apiUrl()` prepends.
 */
export const routes = {
  auth: {
    signup: () => '/auth/signup',
    login: () => '/auth/login',
    logout: () => '/auth/logout',
    refresh: () => '/auth/refresh',
    me: () => '/auth/me',
    /** Full-page navigation, so this one needs the /api/v1 prefix baked in. */
    googleStart: () => '/auth/google',
    /** SynergyAuth SSO — full-page navigation, same as googleStart. */
    synergyStart: () => '/auth/synergy',
  },
  channels: {
    me: () => '/channels/me',
    byHandle: (handle: string) => `/channels/${encodeURIComponent(handle)}`,
    videos: (handle: string) => `/channels/${encodeURIComponent(handle)}/videos`,
  },
  videos: {
    detail: (id: string) => `/videos/${encodeURIComponent(id)}`,
    playback: (id: string) => `/videos/${encodeURIComponent(id)}/playback`,
    view: (id: string) => `/videos/${encodeURIComponent(id)}/view`,
    progress: (id: string) => `/videos/${encodeURIComponent(id)}/progress`,
    upNext: (id: string) => `/videos/${encodeURIComponent(id)}/up-next`,
  },
  // Engagement is served as flat collections, not nested under the video: a
  // reaction is /reactions/:videoId, comments are /comments (videoId + parentId
  // as query on GET, in the body on POST), a subscription is
  // /subscriptions/:channelId. Nesting these under /videos or /channels is a 404.
  reactions: (videoId: string) => `/reactions/${encodeURIComponent(videoId)}`,
  comments: {
    /** GET takes ?videoId (+ optional parentId for a thread); POST puts them in the body. */
    list: () => '/comments',
    detail: (id: string) => `/comments/${encodeURIComponent(id)}`,
  },
  subscriptions: (channelId: string) => `/subscriptions/${encodeURIComponent(channelId)}`,
  uploads: {
    create: () => '/uploads',
    detail: (uploadId: string) => `/uploads/${encodeURIComponent(uploadId)}`,
    part: (uploadId: string, partNumber: number) =>
      `/uploads/${encodeURIComponent(uploadId)}/parts/${partNumber}`,
    complete: (uploadId: string) => `/uploads/${encodeURIComponent(uploadId)}/complete`,
  },
  discovery: {
    feed: () => '/feed',
    search: () => '/search',
  },
  notifications: {
    list: () => '/notifications',
    unreadCount: () => '/notifications/unread-count',
    read: () => '/notifications/read',
  },
  history: {
    list: () => '/history',
  },
} as const;
